import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { Loader2, Send, Users, Phone, CheckSquare, Square, AlertCircle, CheckCircle2 } from 'lucide-react';

interface Session {
  id: string;
  session_date: string;
  session_time: string;
  label: string;
  location: string;
  capacity: number;
  verified_enrollment_count: number;
}

interface Enrollment {
  id: number;
  parentName: string;
  phone: string;
  whatsappPhone: string;
  gender: string;
  age: string;
  district: string;
  attendees: number;
  sessionDate: string;
  sessionTime: string;
  verified: boolean;
}

interface Template {
  name: string;
  language: string;
  category: string;
  status: string;
  variableCount: number;
  components: Array<any>;
}

interface VariableMapping {
  source: 'field' | 'static';
  field?: string;
  value?: string;
}

const AUTO_FIELDS = [
  { value: 'parent_name', label: '家長姓名' },
  { value: 'phone', label: '電話號碼' },
  { value: 'session_date', label: '簡介會日期' },
  { value: 'session_time', label: '簡介會時間' },
  { value: 'location', label: '簡介會地點' },
  { value: 'attendees', label: '出席人數' },
];

export default function MessageBroadcastPanel() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [manualPhones, setManualPhones] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [variableMapping, setVariableMapping] = useState<Record<string, VariableMapping>>({});
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === selectedTemplateName) || null,
    [templates, selectedTemplateName]
  );

  // Load sessions
  useEffect(() => {
    setLoadingSessions(true);
    apiFetch('/api/public/admin/goodstore/sessions-summary')
      .then((r) => r.json())
      .then((data: any) => {
        const list = Array.isArray(data.sessions) ? data.sessions : [];
        setSessions(list);
        if (list.length > 0) {
          setSelectedSessionId(list[0].id);
        }
      })
      .catch((e) => console.error('Failed to load sessions', e))
      .finally(() => setLoadingSessions(false));
  }, []);

  // Load enrollments when session changes
  useEffect(() => {
    if (!selectedSessionId) {
      setEnrollments([]);
      return;
    }
    setLoadingEnrollments(true);
    const params = new URLSearchParams();
    const session = sessions.find((s) => s.id === selectedSessionId);
    if (session) {
      params.set('sessionDate', session.session_date);
    }
    apiFetch(`/api/public/admin/goodstore/enrollments?${params.toString()}`)
      .then((r) => r.json())
      .then((data: any) => {
        const list = Array.isArray(data) ? data : [];
        setEnrollments(list);
        setSelectedIds(new Set());
      })
      .catch((e) => console.error('Failed to load enrollments', e))
      .finally(() => setLoadingEnrollments(false));
  }, [selectedSessionId, sessions]);

  // Load approved templates
  useEffect(() => {
    setLoadingTemplates(true);
    apiFetch('/api/public/admin/goodstore/templates')
      .then((r) => r.json())
      .then((data: any) => {
        const list = Array.isArray(data.templates) ? data.templates : [];
        setTemplates(list);
      })
      .catch((e) => console.error('Failed to load templates', e))
      .finally(() => setLoadingTemplates(false));
  }, []);

  // Initialize default variable mapping when template changes
  useEffect(() => {
    if (!selectedTemplate) {
      setVariableMapping({});
      return;
    }
    const defaults: Record<string, VariableMapping> = {};
    const defaultFields = ['parent_name', 'session_date', 'session_time', 'location', 'phone', 'attendees'];
    for (let i = 1; i <= selectedTemplate.variableCount; i++) {
      defaults[String(i)] = {
        source: 'field',
        field: defaultFields[i - 1] || '',
      };
    }
    setVariableMapping(defaults);
  }, [selectedTemplate]);

  const allSelected = enrollments.length > 0 && selectedIds.size === enrollments.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < enrollments.length;

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(enrollments.map((e) => e.id)));
    }
  }

  function toggleOne(id: number) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  }

  function formatPhone(phone: string) {
    const s = String(phone || '');
    if (s.startsWith('852') && s.length === 11) {
      return `${s.slice(0, 3)} ${s.slice(3, 7)} ${s.slice(7)}`;
    }
    return s;
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedSessionId || !selectedTemplateName) {
      setResult({ type: 'error', text: '請選擇場次及 Meta template' });
      return;
    }
    if (selectedIds.size === 0 && !manualPhones.trim()) {
      setResult({ type: 'error', text: '請至少選擇一位報名者或輸入手動電話號碼' });
      return;
    }

    setSending(true);
    setResult(null);
    try {
      const res = await apiFetch('/api/public/admin/goodstore/send-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: selectedSessionId,
          enrollmentIds: Array.from(selectedIds),
          manualPhones,
          templateName: selectedTemplateName,
          language: selectedTemplate?.language || 'zh_HK',
          variableMapping,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '發送失敗');
      setResult({
        type: 'success',
        text: `發送完成：成功 ${data.sent || 0} 人，失敗 ${data.failed || 0} 人（共 ${data.total || 0} 人）`,
      });
      if (data.failed === 0) {
        setSelectedIds(new Set());
        setManualPhones('');
      }
    } catch (err: any) {
      setResult({ type: 'error', text: err.message || '發送失敗' });
    } finally {
      setSending(false);
    }
  }

  if (loadingSessions || loadingTemplates) {
    return (
      <div className="py-12 text-center text-gray-500">
        <Loader2 size={24} className="animate-spin mx-auto mb-2" />
        載入中...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
        <p className="font-bold mb-1">Template 設置位置：</p>
        <p>請到後台「WhatsApp Center」建立或同步 Meta 模板。此處只會發送已通過審批（APPROVED）的模板。</p>
      </div>

      <form onSubmit={handleSend} className="space-y-6">
        {/* Session & Template selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">簡介會場次 *</label>
            <select
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              required
            >
              <option value="">選擇場次</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label} · {s.location}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Meta Template *</label>
            <select
              value={selectedTemplateName}
              onChange={(e) => setSelectedTemplateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              required
            >
              <option value="">選擇 Template</option>
              {templates.map((t) => (
                <option key={`${t.name}-${t.language}`} value={t.name}>
                  {t.name}（{t.language}）· {t.variableCount} 個變數
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Variable mapping */}
        {selectedTemplate && selectedTemplate.variableCount > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3">Template 變數對應</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: selectedTemplate.variableCount }, (_, i) => i + 1).map((idx) => {
                const mapping = variableMapping[String(idx)] || { source: 'field', field: '' };
                return (
                  <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      {'{'}'{idx}'{'}'} 變數
                    </label>
                    <select
                      value={mapping.source}
                      onChange={(e) =>
                        setVariableMapping((prev) => ({
                          ...prev,
                          [String(idx)]: { source: e.target.value as 'field' | 'static', field: '', value: '' },
                        }))
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm mb-2"
                    >
                      <option value="field">自動欄位</option>
                      <option value="static">固定文字</option>
                    </select>
                    {mapping.source === 'field' ? (
                      <select
                        value={mapping.field || ''}
                        onChange={(e) =>
                          setVariableMapping((prev) => ({
                            ...prev,
                            [String(idx)]: { source: 'field', field: e.target.value },
                          }))
                        }
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                      >
                        <option value="">選擇欄位</option>
                        {AUTO_FIELDS.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={mapping.value || ''}
                        onChange={(e) =>
                          setVariableMapping((prev) => ({
                            ...prev,
                            [String(idx)]: { source: 'static', value: e.target.value },
                          }))
                        }
                        placeholder="輸入固定文字"
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recipients */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enrollment list */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Users size={16} className="text-orange-600" />
                報名名單
              </h3>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-xs font-bold text-orange-700 hover:text-orange-800 flex items-center gap-1"
              >
                {allSelected ? <CheckSquare size={14} /> : someSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                {allSelected ? '取消全選' : '全選'}
              </button>
            </div>

            {loadingEnrollments ? (
              <div className="py-8 text-center text-gray-500">
                <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                載入中...
              </div>
            ) : enrollments.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">暫無報名記錄</div>
            ) : (
              <div className="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                          }}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 text-orange-600 rounded"
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">家長姓名</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">電話</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">狀態</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {enrollments.map((e) => (
                      <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(e.id)}
                            onChange={() => toggleOne(e.id)}
                            className="w-4 h-4 text-orange-600 rounded"
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-800 dark:text-white">{e.parentName}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300 font-mono">{formatPhone(e.whatsappPhone || e.phone)}</td>
                        <td className="px-3 py-2">
                          {e.verified ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">已驗證</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">待驗證</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">已選擇 {selectedIds.size} 位報名者</p>
          </div>

          {/* Manual phones */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-3">
              <Phone size={16} className="text-orange-600" />
              手動輸入（CSV 格式）
            </h3>
            <textarea
              value={manualPhones}
              onChange={(e) => setManualPhones(e.target.value)}
              placeholder="電話, 變數1, 變數2, 變數3...&#10;例如：&#10;85212345678, 小明, 8月8日介紹會&#10;85287654321, 陳太, 8月15日介紹會"
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              每行一筆記錄。第一欄為電話號碼，後續欄位依序填入 template 變數 {'{'}'{1}'{'}'}、{'{'}'{2}'{'}'}、{'{'}'{3}'{'}'}…。支援英文逗號 "," 及中文逗號 "，" 分隔。
            </p>
          </div>
        </div>

        {/* Result & Submit */}
        {result && (
          <div
            className={`p-3 rounded-lg text-sm font-bold flex items-center gap-2 ${
              result.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {result.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {result.text}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={sending || (!selectedSessionId || !selectedTemplateName)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending && <Loader2 size={16} className="animate-spin" />}
            <Send size={16} />
            {sending ? '發送中...' : '發送訊息'}
          </button>
        </div>
      </form>
    </div>
  );
}
